// src/pages/RelatorioAnual.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Fade,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { keyframes } from "@mui/system";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import EuroRoundedIcon from "@mui/icons-material/EuroRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";

import { db } from "../contexts/firebase/config";
import {
  collection,
  query,
  where,
  onSnapshot,
  Timestamp,
  doc,
  getDoc,
} from "firebase/firestore";
import jsPDF from "jspdf";

const floatGlow = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(0, -18px, 0) scale(1.05); }
`;

const shimmer = keyframes`
  0% { transform: translateX(-120%) skewX(-18deg); }
  100% { transform: translateX(220%) skewX(-18deg); }
`;

const YEARS = ["todos", 2024, 2025, 2026];

function formatEuro(value) {
  const num = Number(value || 0);

  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency: "EUR",
  }).format(num);
}

function getYearLabel(year) {
  return year === "todos" ? "Todos os anos" : String(year);
}

function getStatusLabel(status) {
  if (status === "pendentes") return "Pendentes";
  if (status === "pagas") return "Pagas";
  return "Todos";
}

function statusColor(status) {
  return status === "Pago" ? "success" : "warning";
}

function StatCard({ icon, label, value, tone = "primary" }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: "26px",
        border: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: (t) =>
          t.palette.mode === "dark"
            ? "rgba(255,255,255,0.045)"
            : "rgba(255,255,255,0.82)",
        backdropFilter: "blur(18px)",
        overflow: "hidden",
        position: "relative",
        transition: "all .22s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: (t) =>
            t.palette.mode === "dark"
              ? "0 22px 60px rgba(0,0,0,0.35)"
              : "0 22px 60px rgba(15,23,42,0.10)",
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.6} alignItems="center">
          <Avatar
            sx={{
              width: 52,
              height: 52,
              borderRadius: "18px",
              bgcolor: (t) => alpha(t.palette[tone].main, 0.12),
              color: `${tone}.main`,
              border: (t) => `1px solid ${alpha(t.palette[tone].main, 0.18)}`,
            }}
          >
            {icon}
          </Avatar>

          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 800 }}
            >
              {label}
            </Typography>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 950,
                letterSpacing: "-0.055em",
                lineHeight: 1.1,
              }}
            >
              {value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function RelatorioAnual() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const currentYear = new Date().getFullYear();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [statusFilter, setStatusFilter] = useState("todos");
  const [empresaFilter, setEmpresaFilter] = useState("todas");
  const [relatorio, setRelatorio] = useState({});
  const [partnerNames, setPartnerNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    setLoading(true);
    setLoadError("");

    let startDate;
    let endDate;

    if (selectedYear === "todos") {
      startDate = new Date(2024, 0, 1);
      endDate = new Date(2027, 0, 1);
    } else {
      startDate = new Date(Number(selectedYear), 0, 1);
      endDate = new Date(Number(selectedYear) + 1, 0, 1);
    }

    const comprasQuery = query(
      collection(db, "compras"),
      where("dataCompra", ">=", Timestamp.fromDate(startDate)),
      where("dataCompra", "<", Timestamp.fromDate(endDate))
    );

    const unsubscribe = onSnapshot(
      comprasQuery,
      async (snapshot) => {
        try {
          const grouped = {};

          snapshot.docs.forEach((document) => {
            const data = document.data();
            const empresaId = data.empresaId || "sem-empresa";

            if (!grouped[empresaId]) {
              grouped[empresaId] = {
                total: 0,
                compras: [],
              };
            }

            const comissao = Number(data.comissao || 0);
            const dataCompra = data.dataCompra?.toDate?.();

            grouped[empresaId].total += comissao;
            grouped[empresaId].compras.push({
              id: document.id,
              nome: data.clienteNome || "Cliente sem nome",
              ano: dataCompra ? dataCompra.getFullYear() : "—",
              data: dataCompra,
              comissao,
              status: data.status || "Pendente",
            });
          });

          setRelatorio(grouped);

          const names = {};

          await Promise.all(
            Object.keys(grouped).map(async (empresaId) => {
              if (empresaId === "sem-empresa") {
                names[empresaId] = "Sem empresa associada";
                return;
              }

              const empresaSnap = await getDoc(doc(db, "empresas", empresaId));

              names[empresaId] = empresaSnap.exists()
                ? empresaSnap.data().nomeEmpresa || `Empresa #${empresaId.slice(-5)}`
                : `Empresa #${empresaId.slice(-5)}`;
            })
          );

          setPartnerNames(names);
          setLoading(false);
        } catch (error) {
          console.error(error);
          setLoadError("Não foi possível carregar o relatório.");
          setLoading(false);
        }
      },
      (error) => {
        console.error(error);
        setLoadError("Erro ao ligar ao relatório em tempo real.");
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [selectedYear]);

  const allEmpresaIds = useMemo(() => Object.keys(relatorio), [relatorio]);

  const reportGroups = useMemo(() => {
    return allEmpresaIds
      .filter((empresaId) => empresaFilter === "todas" || empresaId === empresaFilter)
      .map((empresaId) => {
        const group = relatorio[empresaId];

        const comprasFiltradas = group.compras
          .filter((compra) => {
            if (statusFilter === "todos") return true;
            if (statusFilter === "pendentes") return compra.status !== "Pago";
            return compra.status === "Pago";
          })
          .sort((a, b) => {
            const dateA = a.data ? a.data.getTime() : 0;
            const dateB = b.data ? b.data.getTime() : 0;
            return dateB - dateA;
          });

        const total = comprasFiltradas.reduce(
          (acc, compra) => acc + Number(compra.comissao || 0),
          0
        );

        return {
          empresaId,
          empresaNome: partnerNames[empresaId] || empresaId,
          compras: comprasFiltradas,
          total,
        };
      })
      .filter((group) => {
        if (statusFilter === "todos") return true;
        return group.compras.length > 0;
      })
      .sort((a, b) => b.total - a.total);
  }, [allEmpresaIds, empresaFilter, partnerNames, relatorio, statusFilter]);

  const totalComissoes = useMemo(
    () => reportGroups.reduce((acc, group) => acc + group.total, 0),
    [reportGroups]
  );

  const totalRegistos = useMemo(
    () => reportGroups.reduce((acc, group) => acc + group.compras.length, 0),
    [reportGroups]
  );

  const totalParceiros = reportGroups.length;

  const totalPagas = useMemo(
    () =>
      reportGroups.reduce(
        (acc, group) =>
          acc + group.compras.filter((compra) => compra.status === "Pago").length,
        0
      ),
    [reportGroups]
  );

  const totalPendentes = totalRegistos - totalPagas;

  const hasAny = reportGroups.length > 0;

  const downloadPdf = () => {
    const pdf = new jsPDF("p", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const marginX = 40;
    const contentWidth = pageWidth - marginX * 2;
    const bottomSafe = 52;

    let y = 42;

    const ensureSpace = (needed = 28) => {
      if (y + needed > pageHeight - bottomSafe) {
        pdf.addPage();
        drawHeader(pdf.getCurrentPageInfo().pageNumber);
      }
    };

    const drawHeader = (pageNumber) => {
      pdf.setFillColor(15, 23, 42);
      pdf.roundedRect(marginX, 28, contentWidth, 64, 16, 16, "F");

      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(18);
      pdf.text("Relatório Anual de Comissões", marginX + 18, 55);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.setTextColor(203, 213, 225);
      pdf.text(`Período: ${getYearLabel(selectedYear)}`, marginX + 18, 73);

      pdf.text(`Página ${pageNumber}`, pageWidth - marginX - 62, 73);

      y = 116;
    };

    const drawFooter = () => {
      const totalPages = pdf.getNumberOfPages();

      for (let page = 1; page <= totalPages; page += 1) {
        pdf.setPage(page);

        pdf.setDrawColor(226, 232, 240);
        pdf.line(marginX, pageHeight - 32, pageWidth - marginX, pageHeight - 32);

        pdf.setTextColor(100, 116, 139);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);

        pdf.text(
          "Documento gerado automaticamente pela plataforma Sintanet",
          marginX,
          pageHeight - 16
        );

        pdf.text(
          `Página ${page} de ${totalPages}`,
          pageWidth - marginX - 58,
          pageHeight - 16
        );
      }
    };

    const drawMetric = (x, cardY, width, label, value) => {
      pdf.setFillColor(248, 250, 252);
      pdf.setDrawColor(226, 232, 240);
      pdf.roundedRect(x, cardY, width, 62, 12, 12, "FD");

      pdf.setTextColor(100, 116, 139);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.text(label, x + 12, cardY + 20);

      pdf.setTextColor(15, 23, 42);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(17);
      pdf.text(String(value), x + 12, cardY + 43);
    };

    const drawTableHeader = () => {
      ensureSpace(30);

      pdf.setFillColor(241, 245, 249);
      pdf.setDrawColor(226, 232, 240);
      pdf.rect(marginX, y, contentWidth, 26, "FD");

      pdf.setTextColor(51, 65, 85);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);

      pdf.text("Cliente", marginX + 10, y + 17);
      pdf.text("Ano", marginX + 260, y + 17);
      pdf.text("Comissão", marginX + 330, y + 17);
      pdf.text("Estado", marginX + 440, y + 17);

      y += 26;
    };

    drawHeader(1);

    pdf.setTextColor(71, 85, 105);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text(
      `Filtros — Ano: ${getYearLabel(selectedYear)} | Estado: ${getStatusLabel(
        statusFilter
      )} | Parceiro: ${
        empresaFilter === "todas"
          ? "Todos"
          : partnerNames[empresaFilter] || empresaFilter
      }`,
      marginX,
      y
    );

    y += 24;

    const gap = 12;
    const cardWidth = (contentWidth - gap * 2) / 3;

    drawMetric(marginX, y, cardWidth, "Parceiros", totalParceiros);
    drawMetric(marginX + cardWidth + gap, y, cardWidth, "Registos", totalRegistos);
    drawMetric(
      marginX + (cardWidth + gap) * 2,
      y,
      cardWidth,
      "Total",
      formatEuro(totalComissoes)
    );

    y += 86;

    reportGroups.forEach((group) => {
      ensureSpace(58);

      pdf.setFillColor(255, 255, 255);
      pdf.setDrawColor(226, 232, 240);
      pdf.roundedRect(marginX, y, contentWidth, 38, 10, 10, "FD");

      pdf.setTextColor(15, 23, 42);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12);
      pdf.text(group.empresaNome, marginX + 12, y + 24);

      pdf.setTextColor(37, 99, 235);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.text(`Total: ${formatEuro(group.total)}`, pageWidth - marginX - 105, y + 24);

      y += 50;

      if (group.compras.length === 0) {
        ensureSpace(24);
        pdf.setTextColor(100, 116, 139);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);
        pdf.text("Sem registos para os filtros aplicados.", marginX + 4, y);
        y += 28;
        return;
      }

      drawTableHeader();

      group.compras.forEach((compra, index) => {
        ensureSpace(25);

        if (index % 2 === 0) {
          pdf.setFillColor(248, 250, 252);
          pdf.rect(marginX, y, contentWidth, 24, "F");
        }

        pdf.setTextColor(15, 23, 42);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);

        const cliente = String(compra.nome || "—");
        const clienteCurto =
          cliente.length > 34 ? `${cliente.slice(0, 31)}...` : cliente;

        pdf.text(clienteCurto, marginX + 10, y + 16);
        pdf.text(String(compra.ano || "—"), marginX + 260, y + 16);
        pdf.text(formatEuro(compra.comissao), marginX + 330, y + 16);

        const pago = compra.status === "Pago";

        pdf.setTextColor(pago ? 22 : 180, pago ? 163 : 120, pago ? 74 : 30);
        pdf.text(pago ? "Pago" : "Pendente", marginX + 440, y + 16);

        y += 24;
      });

      y += 18;
    });

    drawFooter();
    pdf.save(`relatorio-anual-comissoes-${selectedYear}.pdf`);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 5 },
        position: "relative",
        overflow: "hidden",
        bgcolor: "background.default",
        background: isDark
          ? "linear-gradient(135deg, #020617 0%, #0f172a 48%, #111827 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 48%, #ecfeff 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: 520,
          height: 520,
          borderRadius: "50%",
          top: -220,
          right: -160,
          bgcolor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.14),
          filter: "blur(80px)",
          animation: `${floatGlow} 9s ease-in-out infinite`,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: 460,
          height: 460,
          borderRadius: "50%",
          bottom: -190,
          left: -160,
          bgcolor: alpha("#06b6d4", isDark ? 0.16 : 0.13),
          filter: "blur(80px)",
          animation: `${floatGlow} 11s ease-in-out infinite`,
          animationDelay: "1.2s",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: isDark ? 0.14 : 0.28,
          pointerEvents: "none",
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.16) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "linear-gradient(to bottom, black, transparent 80%)",
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2 }}>
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            overflow: "hidden",
            borderRadius: { xs: "28px", md: "40px" },
            border: (t) =>
              `1px solid ${
                t.palette.mode === "dark"
                  ? "rgba(255,255,255,0.10)"
                  : "rgba(255,255,255,0.78)"
              }`,
            bgcolor: isDark ? "rgba(15,23,42,0.72)" : "rgba(255,255,255,0.72)",
            backdropFilter: "blur(26px)",
            WebkitBackdropFilter: "blur(26px)",
            boxShadow: isDark
              ? "0 34px 110px rgba(0,0,0,0.46)"
              : "0 34px 110px rgba(15,23,42,0.13)",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background: `
                radial-gradient(720px 320px at 12% 0%, ${alpha(
                  theme.palette.primary.main,
                  isDark ? 0.18 : 0.12
                )}, transparent 62%),
                radial-gradient(640px 320px at 96% 0%, ${alpha(
                  "#06b6d4",
                  isDark ? 0.15 : 0.12
                )}, transparent 60%)
              `,
            }}
          />

          <Box sx={{ position: "relative", zIndex: 1, p: { xs: 2.5, md: 4 } }}>
            <Stack spacing={3}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", md: "flex-start" }}
                spacing={2.5}
              >
                <Stack spacing={1.5}>
                  <Button
                    component={RouterLink}
                    to="/admin"
                    startIcon={<ArrowBackRoundedIcon />}
                    variant="outlined"
                    size="small"
                    sx={{
                      width: "fit-content",
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 850,
                      bgcolor: "background.paper",
                    }}
                  >
                    Voltar ao painel
                  </Button>

                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      sx={{
                        width: 62,
                        height: 62,
                        borderRadius: "22px",
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: "primary.main",
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                        boxShadow: `0 18px 42px ${alpha(
                          theme.palette.primary.main,
                          0.16
                        )}`,
                      }}
                    >
                      <AssessmentRoundedIcon />
                    </Avatar>

                    <Box>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 950,
                          letterSpacing: "-0.065em",
                          fontSize: { xs: 32, md: 48 },
                          lineHeight: 1,
                        }}
                      >
                        Relatório Anual
                      </Typography>

                      <Typography color="text.secondary" sx={{ mt: 0.8 }}>
                        Comissões por parceiro, estado e período em tempo real.
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.2}
                  alignItems={{ xs: "stretch", sm: "center" }}
                >
                  <Chip
                    icon={<AutoAwesomeRoundedIcon />}
                    label="Live Report"
                    sx={{
                      height: 40,
                      borderRadius: 999,
                      fontWeight: 950,
                      bgcolor: alpha(theme.palette.success.main, isDark ? 0.16 : 0.1),
                      color: "success.main",
                      border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
                    }}
                  />

                  <Button
                    variant="contained"
                    startIcon={<PictureAsPdfRoundedIcon />}
                    onClick={downloadPdf}
                    disabled={!hasAny || loading}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: 999,
                      px: 3,
                      py: 1.25,
                      fontWeight: 950,
                      textTransform: "none",
                      boxShadow: `0 18px 42px ${alpha(
                        theme.palette.primary.main,
                        0.28
                      )}`,
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        width: "42%",
                        background:
                          "linear-gradient(90deg, transparent, rgba(255,255,255,.32), transparent)",
                        animation: `${shimmer} 3.2s ease-in-out infinite`,
                      },
                    }}
                  >
                    Exportar PDF
                  </Button>
                </Stack>
              </Stack>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(4, 1fr)",
                  },
                  gap: 2,
                }}
              >
                <StatCard
                  icon={<BusinessRoundedIcon />}
                  label="Parceiros"
                  value={totalParceiros}
                  tone="primary"
                />

                <StatCard
                  icon={<ReceiptLongRoundedIcon />}
                  label="Registos"
                  value={totalRegistos}
                  tone="secondary"
                />

                <StatCard
                  icon={<EuroRoundedIcon />}
                  label="Total comissões"
                  value={formatEuro(totalComissoes)}
                  tone="success"
                />

                <StatCard
                  icon={<PendingActionsRoundedIcon />}
                  label="Pendentes"
                  value={totalPendentes}
                  tone="warning"
                />
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "26px",
                  border: (t) => `1px solid ${t.palette.divider}`,
                  bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(248,250,252,0.82)",
                  backdropFilter: "blur(16px)",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  alignItems={{ xs: "stretch", md: "center" }}
                >
                  <Chip
                    icon={<FilterAltRoundedIcon />}
                    label="Filtros"
                    sx={{
                      height: 40,
                      fontWeight: 950,
                      borderRadius: 999,
                      bgcolor: "background.paper",
                    }}
                  />

                  <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 170 } }}>
                    <InputLabel>Ano</InputLabel>
                    <Select
                      value={selectedYear}
                      label="Ano"
                      onChange={(event) => setSelectedYear(event.target.value)}
                      sx={{ borderRadius: "16px", bgcolor: "background.paper" }}
                    >
                      {YEARS.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year === "todos" ? "Todos os anos" : year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 170 } }}>
                    <InputLabel>Estado</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Estado"
                      onChange={(event) => setStatusFilter(event.target.value)}
                      sx={{ borderRadius: "16px", bgcolor: "background.paper" }}
                    >
                      <MenuItem value="todos">Todos</MenuItem>
                      <MenuItem value="pendentes">Pendentes</MenuItem>
                      <MenuItem value="pagas">Pagas</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 260 } }}>
                    <InputLabel>Parceiro</InputLabel>
                    <Select
                      value={empresaFilter}
                      label="Parceiro"
                      onChange={(event) => setEmpresaFilter(event.target.value)}
                      sx={{ borderRadius: "16px", bgcolor: "background.paper" }}
                    >
                      <MenuItem value="todas">Todas as empresas</MenuItem>

                      {allEmpresaIds.map((empresaId) => (
                        <MenuItem key={empresaId} value={empresaId}>
                          {partnerNames[empresaId] || empresaId}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box sx={{ flexGrow: 1 }} />

                  <Chip
                    icon={<CalendarMonthRoundedIcon />}
                    label={getYearLabel(selectedYear)}
                    sx={{ borderRadius: 999, fontWeight: 850 }}
                  />

                  <Chip
                    icon={<GroupsRoundedIcon />}
                    label={`${totalParceiros} parceiro(s)`}
                    sx={{ borderRadius: 999, fontWeight: 850 }}
                  />
                </Stack>
              </Paper>
            </Stack>
          </Box>
        </Paper>

        {loadError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: "18px" }}>
            {loadError}
          </Alert>
        )}

        {loading ? (
          <Paper
            elevation={0}
            sx={{
              p: 8,
              borderRadius: "32px",
              textAlign: "center",
              border: (t) => `1px solid ${t.palette.divider}`,
              bgcolor: isDark ? "rgba(15,23,42,0.72)" : "rgba(255,255,255,0.8)",
              backdropFilter: "blur(20px)",
            }}
          >
            <CircularProgress />
            <Typography sx={{ mt: 2, fontWeight: 850 }}>
              A carregar relatório...
            </Typography>
            <Typography color="text.secondary" variant="body2">
              A obter dados em tempo real.
            </Typography>
          </Paper>
        ) : !hasAny ? (
          <Fade in>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 7 },
                textAlign: "center",
                borderRadius: "32px",
                border: (t) => `1px dashed ${t.palette.divider}`,
                bgcolor: isDark ? "rgba(15,23,42,0.62)" : "rgba(255,255,255,0.78)",
                backdropFilter: "blur(20px)",
              }}
            >
              <Avatar
                sx={{
                  mx: "auto",
                  mb: 2,
                  width: 70,
                  height: 70,
                  borderRadius: "24px",
                  bgcolor: alpha(theme.palette.warning.main, 0.12),
                  color: "warning.main",
                }}
              >
                <ReceiptLongRoundedIcon />
              </Avatar>

              <Typography variant="h5" fontWeight={950}>
                Nenhuma comissão encontrada
              </Typography>

              <Typography color="text.secondary" sx={{ mt: 0.8 }}>
                Ajusta os filtros para consultar outros parceiros, anos ou estados.
              </Typography>
            </Paper>
          </Fade>
        ) : (
          <Stack spacing={2.2}>
            {reportGroups.map((group, index) => {
              const paidCount = group.compras.filter(
                (compra) => compra.status === "Pago"
              ).length;

              const pendingCount = group.compras.length - paidCount;

              return (
                <Fade in timeout={250 + index * 80} key={group.empresaId}>
                  <Accordion
                    defaultExpanded={index === 0}
                    elevation={0}
                    sx={{
                      borderRadius: "30px !important",
                      overflow: "hidden",
                      border: (t) => `1px solid ${t.palette.divider}`,
                      bgcolor: isDark
                        ? "rgba(15,23,42,0.74)"
                        : "rgba(255,255,255,0.84)",
                      backdropFilter: "blur(22px)",
                      boxShadow: isDark
                        ? "0 22px 64px rgba(0,0,0,0.28)"
                        : "0 22px 64px rgba(15,23,42,0.08)",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreRoundedIcon />}
                      sx={{
                        px: { xs: 2, md: 3 },
                        py: 1.4,
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 2,
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "18px",
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: "primary.main",
                          border: `1px solid ${alpha(
                            theme.palette.primary.main,
                            0.14
                          )}`,
                        }}
                      >
                        <BusinessRoundedIcon />
                      </Avatar>

                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="h6"
                          fontWeight={950}
                          sx={{
                            letterSpacing: "-0.025em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {group.empresaNome}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                          sx={{ mt: 0.8 }}
                        >
                          <Chip
                            size="small"
                            label={`${group.compras.length} registo(s)`}
                            sx={{ borderRadius: 999, fontWeight: 800 }}
                          />

                          <Chip
                            size="small"
                            icon={<CheckCircleRoundedIcon />}
                            label={`${paidCount} pago(s)`}
                            color="success"
                            variant="outlined"
                            sx={{ borderRadius: 999, fontWeight: 800 }}
                          />

                          <Chip
                            size="small"
                            icon={<PendingActionsRoundedIcon />}
                            label={`${pendingCount} pendente(s)`}
                            color="warning"
                            variant="outlined"
                            sx={{ borderRadius: 999, fontWeight: 800 }}
                          />
                        </Stack>
                      </Box>

                      <Chip
                        label={formatEuro(group.total)}
                        color="primary"
                        sx={{
                          display: { xs: "none", sm: "inline-flex" },
                          borderRadius: 999,
                          fontWeight: 950,
                          height: 38,
                          px: 0.5,
                        }}
                      />
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: 0 }}>
                      <TableContainer>
                        <Table size="small" sx={{ minWidth: 760 }}>
                          <TableHead>
                            <TableRow
                              sx={{
                                bgcolor: isDark
                                  ? "rgba(255,255,255,0.045)"
                                  : "rgba(248,250,252,0.92)",
                              }}
                            >
                              <TableCell sx={{ fontWeight: 950 }}>Cliente</TableCell>
                              <TableCell sx={{ fontWeight: 950 }}>Ano</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 950 }}>
                                Comissão
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 950 }}>
                                Estado
                              </TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {group.compras.map((compra) => (
                              <TableRow
                                key={compra.id}
                                hover
                                sx={{
                                  "&:last-child td": { borderBottom: 0 },
                                  "&:hover": {
                                    bgcolor: alpha(theme.palette.primary.main, 0.045),
                                  },
                                }}
                              >
                                <TableCell>
                                  <Stack direction="row" spacing={1.3} alignItems="center">
                                    <Avatar
                                      sx={{
                                        width: 34,
                                        height: 34,
                                        fontSize: ".85rem",
                                        fontWeight: 950,
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        color: "primary.main",
                                      }}
                                    >
                                      {String(compra.nome || "?")
                                        .charAt(0)
                                        .toUpperCase()}
                                    </Avatar>

                                    <Box>
                                      <Typography fontWeight={850}>
                                        {compra.nome || "—"}
                                      </Typography>

                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        ID: {compra.id.slice(0, 8)}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </TableCell>

                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={compra.ano}
                                    sx={{ borderRadius: 999, fontWeight: 800 }}
                                  />
                                </TableCell>

                                <TableCell align="right">
                                  <Typography fontWeight={950}>
                                    {formatEuro(compra.comissao)}
                                  </Typography>
                                </TableCell>

                                <TableCell align="center">
                                  <Chip
                                    size="small"
                                    label={compra.status === "Pago" ? "Pago" : "Pendente"}
                                    color={statusColor(compra.status)}
                                    variant={
                                      compra.status === "Pago" ? "filled" : "outlined"
                                    }
                                    sx={{
                                      borderRadius: 999,
                                      fontWeight: 900,
                                      minWidth: 92,
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Divider />

                      <Box
                        sx={{
                          p: 2.2,
                          display: "flex",
                          justifyContent: "flex-end",
                          bgcolor: isDark
                            ? "rgba(255,255,255,0.025)"
                            : "rgba(248,250,252,0.75)",
                        }}
                      >
                        <Typography color="text.secondary">
                          Total do parceiro:{" "}
                          <Typography
                            component="span"
                            color="primary"
                            fontWeight={950}
                          >
                            {formatEuro(group.total)}
                          </Typography>
                        </Typography>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Fade>
              );
            })}
          </Stack>
        )}
      </Container>
    </Box>
  );
}